import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import ReactDOM from 'react-dom';
import BillPrint from "./bill";
import './Customer.css';

function CustomerIndex() {
  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    fetchtableData();
    setLoading(true);
  }, [isAuthenticated, companyId, userId, token, logout]);

  useEffect(() => {
    fetchtableData();
  }, []);

  const fetchtableData = async () => {
    fetchData('CustomerApi', `/${companyId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          setData(response.response_data);
        }
      })
      .catch((error) => {
        console.error('Error fetching Customer Details:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = async () => {
    deleteData('CustomerApi', `${deleteItemId}`, token)
      .then(() => {
        fetchtableData();
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  function formatDate(dateString) {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  const handlePrint = async (saleid) => {
    try {
      const response = await fetchData('CustomerApi', `/CautionPrint/${saleid}`, token);
      const labelData = response.response_data;

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

      ReactDOM.render(
        <BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
        iframeDocument.body
      );

      iframeDocument.write('</body></html>');
      iframeDocument.close();
      setTimeout(() => {
        iframe.contentWindow.print();
      }, 1000);
    } catch (error) {
      console.error('Error printing Invoice:', error);
    }
  };

  return (
    <div className="customer-container">
      {/* Loading */}
      {loading && (
        <div className="loading-overlay">
          <CircularProgress size={40} className="loading-spinner" />
          <span className="loading-text">Loading...</span>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Customers</h1>
        <div className="header-actions">
          <div className="search-box">
            <SearchIcon />
            <input
              type="text"
              className="search-input"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          <Link to="/CustomerCreate" className="create-btn">
            <AddIcon fontSize="small" />
            Add Customer
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="customer-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Contact</th>
                <th>Address</th>
                <th>Room</th>
                <th>Rent Date</th>
                <th>Leaving Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={item.Id}>
                    <td>{index + 1}</td>
                    <td className="cell-name">{item.name}</td>
                    <td className="cell-contact">
                      {item.contactNumber1}
                      {item.contactNumber2 && `, ${item.contactNumber2}`}
                    </td>
                    <td>{item.addressLine1}</td>
                    <td>
                      <span className="cell-room">{item.roomName}</span>
                    </td>
                    <td className="cell-date">{formatDate(item.rentDate)}</td>
                    <td className="cell-date">{formatDate(item.DateOfLeaving)}</td>
                    <td>
                      <div className="action-buttons">
                        <Link to={`/CustomerEdit/${item.id}`}>
                          <button className="action-btn edit" title="Edit">
                            <EditIcon fontSize="small" />
                          </button>
                        </Link>
                        <button
                          className="action-btn delete"
                          title="Delete"
                          onClick={() => openConfirmDialog(item.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                        <button
                          className="action-btn print"
                          title="Print"
                          onClick={() => handlePrint(item.id)}
                        >
                          <PrintIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">
                    <div className="empty-state">
                      <p>No customers found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
        <DialogTitle sx={{ fontWeight: 600, color: '#1e3a5f' }}>
          Delete Customer?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#64748b' }}>
            Are you sure you want to delete this customer? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <button className="dialog-btn cancel" onClick={closeConfirmDialog}>
            Cancel
          </button>
          <button className="dialog-btn confirm" onClick={handleDelete}>
            Delete
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CustomerIndex;
